// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { NavigateFunction } from "react-router-dom";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { Dropdown, Form } from "react-bootstrap";

import { IAction, ActionType, raiseAction } from "actions";

import { isSet } from "shared/utils/utils";
import { IState } from "states/states";
import { Site } from "states/sitestates";
import { AppState } from "states/schedulerstates";
import { MainPanel } from "types";
import {
  getCanModifySites,
  getCanModifySiteSettings,
  getInitials,
  getIsSiteOffline,
} from "guiutils";
import SiteModel, { IGeoBounds } from "model/site";

import SearchFilter from "./SearchFilter";
import SiteInfoTags, { getAlarmDescription, getConnectedSiteAlarmCounts } from "./SiteInfoTags";

import IconMore from "icons/iconMore";
import IconDashboard from "icons/iconDashboard";
import IconPowerOn from "icons/iconPowerOn";
import IconPowerOff from "icons/iconPowerOff";
import IconSettings from "icons/iconSettings";
import IconInfo from "icons/iconInfo";
import IconLocation from "icons/iconLocation";

import IconFavorite from "icons/iconFavorite";
import IconEmptyFavorite from "icons/iconEmptyFavorite";

import "./SitesList.scss";

interface ISitesListExtProps {
  onDetails: (id: number) => void;
  // undefined if no map is shown
  onShowInMap?: (id: number) => void;
  filterText: string;
  onFilterTextChange: (filterText: string) => void;
  // map is shown, so the map filters are offered
  canFilterByView: boolean;
  onlyShowInView: boolean;
  onOnlyShowInViewChange: (onlyShowInView: boolean) => void;
  onlyShowWithoutCoordinates: boolean;
  onOnlyShowWithoutCoordinatesChange: (onlyShowWithoutCoordinates: boolean) => void;
  viewportBounds: IGeoBounds | undefined;
}

interface ISitesListProps {
  appState: AppState;
  siteList: Site[] | undefined; // undefined if not loaded yet
  selectedSite: number | undefined;
  favoriteSites: number[] | undefined;
  isOffline: boolean;
  canModifySiteSettings: boolean;
  // for the "only show sites without coordinates" maintenance filter
  canModifySites: boolean;
  connectedUnackAlarms: number | undefined;
  connectedUnclearedAlarms: number | undefined;
}

type SitesListDropdownOptionValue =
  | "connect"
  | "disconnect"
  | "setAsFavorite"
  | "removeAsFavorite"
  | "dashboard"
  | "settings"
  | "details"
  | "showInMap"
  | "separator";

interface ISitesListDrowdownOption {
  value: SitesListDropdownOptionValue;
  label: string;
  seperator: boolean;
  siteId: number;
  disabled?: boolean;
}

const mapStateToProps = (state: IState) => {
  const props: ISitesListProps = {
    appState: state.scheduler.appState,
    siteList: state.site.siteList,
    selectedSite: state.cached.selectedSite,
    favoriteSites: state.cached.favoriteSites,
    isOffline: getIsSiteOffline(state),
    canModifySiteSettings: getCanModifySiteSettings(state),
    canModifySites: getCanModifySites(state),
    ...getConnectedSiteAlarmCounts(state),
  };
  return props;
};

interface ISitesListState {
  // .sites-table's scrollbar width, applied as padding to the connected-site row to
  // align both; measured at runtime as it varies by browser/OS
  scrollbarWidth: number;
  // site with the open option menu, so only one is open at a time
  openMenuSiteId: number | undefined;
}

interface ISitesListDispatch {
  onSelectSite: (id: number) => void;
  onDashboardClick: (navigate: NavigateFunction) => void;
  onDropdownSelectionChanged: (
    selected: ISitesListDrowdownOption,
    navigate: NavigateFunction,
  ) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISitesListDispatch = {
    onSelectSite: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_SWITCH_SITE, id));
    },
    onDashboardClick: (navigate: NavigateFunction) => {
      navigate(`../${MainPanel.DASHBOARD}`);
    },
    onDropdownSelectionChanged: (
      selected: ISitesListDrowdownOption,
      navigate: NavigateFunction,
    ) => {
      switch (selected.value) {
        case "disconnect":
          dispatch(raiseAction(ActionType.ACT_DISCONNECT_SITE));
          break;
        case "connect":
          dispatch(raiseAction(ActionType.ACT_SWITCH_SITE, selected.siteId));
          break;
        case "setAsFavorite":
        case "removeAsFavorite":
          dispatch(
            raiseAction(ActionType.ACT_SET_FAVORITE, {
              doSet: selected.value === "setAsFavorite",
              siteId: selected.siteId,
            }),
          );
          break;
        case "dashboard":
          navigate(`../${MainPanel.DASHBOARD}`);
          break;
        case "settings":
          navigate(`../${MainPanel.SITE_SETTINGS}`);
          break;
      }
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_show_dashboard: {
    id: "Show dashboard",
  },
  msg_show_settings: {
    id: "Show settings",
  },
  msg_show_details: {
    id: "Show details",
  },
  msg_show_in_map: {
    id: "Show in map",
  },
  msg_set_favorite: {
    id: "Mark as favorite",
  },
  msg_remove_favorite: {
    id: "Remove from favorites",
  },
  msg_disconnect: {
    id: "Disconnect",
  },
  msg_connect: {
    id: "Connect",
  },
  msg_connect2site: {
    id: "Connect to Site",
  },
  msg_loading_sites: {
    id: "Loading the list of available sites. Please wait.",
  },
  msg_no_site_available: {
    id: "Currently, there is no site available, which you can connect to.",
  },
  msg_contact_admin: {
    id: "Please contact the system administrator to assign a site to your user.",
  },
  msg_searchfield_placeholder: {
    id: "Type to filter sites...",
  },
  msg_only_show_in_view: {
    id: "Only show sites in view",
  },
  msg_only_show_without_coordinates: {
    id: "Only show sites without coordinates",
  },
  msg_site_count: {
    id: "{count} Sites",
  },
  msg_selected_site_heading: {
    id: "Selected Site",
  },
  msg_no_site_selected: {
    id: "No site selected",
  },
  msg_select_site_hint: {
    id: "Please select any site from the list below to connect.",
  },
});

export function renderIconForOption(type: SitesListDropdownOptionValue): JSX.Element {
  switch (type) {
    case "connect":
      return <IconPowerOn></IconPowerOn>;
    case "disconnect":
      return <IconPowerOff></IconPowerOff>;
    case "details":
      return <IconInfo></IconInfo>;
    case "showInMap":
      return <IconLocation></IconLocation>;
    case "dashboard":
      return <IconDashboard></IconDashboard>;
    case "settings":
      return <IconSettings></IconSettings>;
    case "setAsFavorite":
    case "removeAsFavorite":
      return <IconEmptyFavorite></IconEmptyFavorite>;
    case "separator":
      return <></>;
  }
}

// icon-only toggle for the per-site menu
const SiteMenuToggle = React.forwardRef<
  HTMLDivElement,
  { onClick?: (event: React.MouseEvent) => void }
>(({ onClick }, ref) => (
  <div
    ref={ref}
    className="siteMenuToggle"
    onClick={(event) => {
      event.preventDefault();
      onClick?.(event);
    }}
  >
    <IconMore viewBox="0 0 48 48" />
  </div>
));
SiteMenuToggle.displayName = "SiteMenuToggle";

class SitesListComponent extends React.Component<
  ISitesListProps & ISitesListExtProps & ISitesListDispatch & RoutedProps,
  ISitesListState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: ISitesListState = {
    scrollbarWidth: 0,
    openMenuSiteId: undefined,
  };

  private sitesTableRef = React.createRef<HTMLDivElement>();

  private measureScrollbarWidth = () => {
    const el = this.sitesTableRef.current;
    if (!el) {
      return;
    }
    const scrollbarWidth = el.offsetWidth - el.clientWidth;
    if (scrollbarWidth !== this.state.scrollbarWidth) {
      this.setState({ scrollbarWidth });
    }
  };

  componentDidMount() {
    this.measureScrollbarWidth();
  }

  componentDidUpdate() {
    // the table and its scrollbar only appear after mount, so re-check on updates
    this.measureScrollbarWidth();
  }

  // sorted (favorites first) list, independent of the search text
  private sortedSiteListCache: {
    siteList?: Site[];
    favoriteSites?: number[];
    result: Site[];
  } = { result: [] };

  private getSortedSiteList(): Site[] {
    const { siteList, favoriteSites } = this.props;
    const cache = this.sortedSiteListCache;
    if (cache.siteList === siteList && cache.favoriteSites === favoriteSites) {
      return cache.result;
    }

    const sites = siteList ?? [];

    const favorites = (siteList?.filter((site) => favoriteSites?.includes(site.id)) ?? []).sort(
      (a, b) => a.name.localeCompare(b.name),
    );
    const favoriteIds = new Set(favorites.map((site) => site.id));

    const result = [
      ...favorites,
      ...sites
        .filter((site) => !favoriteIds.has(site.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ];

    this.sortedSiteListCache = { siteList, favoriteSites, result };
    return result;
  }

  private getTableRows(): JSX.Element[] {
    const { selectedSite, onlyShowInView, viewportBounds, onlyShowWithoutCoordinates } = this.props;
    return this.getSortedSiteList()
      .filter((item) => {
        return (
          SiteModel.matchesFilterText(item, this.props.filterText) &&
          (isSet(selectedSite) ? selectedSite !== item.id : true) &&
          (!onlyShowInView || SiteModel.isWithinBounds(item, viewportBounds)) &&
          (!this.props.canFilterByView ||
            !this.props.canModifySites ||
            !onlyShowWithoutCoordinates ||
            !SiteModel.hasValidCoordinates(item))
        );
      })
      .map((item) => this.renderTableRow(item, false));
  }

  public render() {
    const showList =
      this.props.appState === AppState.SITE_LIST_LOADED || this.props.appState === AppState.READY;
    return (
      <div className="sitesDiv">
        {showList && this.renderSearchField()}
        {this.props.siteList === undefined ?
          this.renderLoadingSitelist()
        : this.props.selectedSite === undefined ?
          this.props.siteList.length === 0 ?
            this.renderNoSite()
          : this.renderNoCurrentSiteInfo()
        : this.renderCurrentSite()}

        {showList && this.renderSiteList()}
      </div>
    );
  }

  private renderSiteList() {
    const table_rows = this.getTableRows();

    return (
      <>
        <div>
          <h1 style={{ display: "flex" }}>
            <FormattedMessage
              id={messages.msg_site_count.id}
              values={{ count: table_rows.length }}
            />
          </h1>
        </div>
        <div className="sites-table" ref={this.sitesTableRef}>
          <ul id="siteList" className="sites scrollbar site-row">
            {table_rows}
          </ul>
        </div>
      </>
    );
  }

  private renderCurrentSite() {
    const site: Site | undefined = this.props.siteList!.filter(
      (curSite) => curSite.id === this.props.selectedSite,
    )[0];

    if (!isSet(site)) {
      return;
    }

    return (
      <>
        <h1>
          <FormattedMessage id={messages.msg_selected_site_heading.id} />
        </h1>
        <div className="">
          <ul
            id="selectedSite"
            className="sites scrollbar site-row"
            style={{ paddingRight: this.state.scrollbarWidth }}
          >
            {this.renderTableRow(site, true, this.props.isOffline, "bottom")}
          </ul>
        </div>
      </>
    );
  }

  private renderLoadingSitelist() {
    return (
      <>
        <h1>
          <FormattedMessage id={messages.msg_connect2site.id} />
        </h1>
        <p>
          <FormattedMessage id={messages.msg_loading_sites.id} />
        </p>
      </>
    );
  }

  private renderNoSite() {
    return (
      <>
        <h1>
          <FormattedMessage id={messages.msg_connect2site.id} />
        </h1>
        <p>
          <FormattedMessage id={messages.msg_no_site_available.id} />
        </p>
        <p>
          <FormattedMessage id={messages.msg_contact_admin.id} />
        </p>
      </>
    );
  }

  private renderNoCurrentSiteInfo() {
    return (
      <div className="noCurrentSiteInfo">
        <h1>
          <FormattedMessage id={messages.msg_no_site_selected.id} />
        </h1>
        <p>
          <FormattedMessage id={messages.msg_select_site_hint.id} />
        </p>
      </div>
    );
  }

  private renderSearchField() {
    return (
      <>
        <SearchFilter
          id="searchField"
          tabIndex={0}
          autoFocus={true}
          className="sitesSearchBox"
          filterText={this.props.filterText}
          placeholder={this.context.formatMessage(messages.msg_searchfield_placeholder)}
          onChange={this.props.onFilterTextChange}
          // filtering the list and map on every keystroke would lag
          debounceMs={500}
        />
        <div className="d-flex">
          {this.props.canFilterByView && (
            <div className="me-3 link" style={{ marginTop: 11 }}>
              <Form.Check
                id="onlyShowInView"
                className="me-2"
                style={{ marginTop: 3 }}
                checked={this.props.onlyShowInView}
                onChange={(event) =>
                  this.props.onOnlyShowInViewChange(event?.currentTarget.checked)
                }
                label={this.context.formatMessage(messages.msg_only_show_in_view)}
              />
            </div>
          )}
          {this.props.canFilterByView && this.props.canModifySites && (
            <div className="me-3 link" style={{ marginTop: 11 }}>
              <Form.Check
                id="onlyShowWithoutCoordinates"
                className="me-2"
                style={{ marginTop: 3 }}
                checked={this.props.onlyShowWithoutCoordinates}
                onChange={(event) =>
                  this.props.onOnlyShowWithoutCoordinatesChange(event?.currentTarget.checked)
                }
                label={this.context.formatMessage(messages.msg_only_show_without_coordinates)}
              />
            </div>
          )}
        </div>
      </>
    );
  }

  private getDropdownSeparator(): ISitesListDrowdownOption {
    return { value: "separator", label: "---", seperator: true, siteId: -1 };
  }

  private getFavoriteDropdownOption(site: Site): ISitesListDrowdownOption {
    const isFavorite = this.props.favoriteSites?.includes(site.id) ?? false;
    return {
      value: isFavorite ? "removeAsFavorite" : "setAsFavorite",
      label: this.context.formatMessage(
        isFavorite ? messages.msg_remove_favorite : messages.msg_set_favorite,
      ),
      seperator: false,
      siteId: site.id,
    };
  }

  private getDropdownOptions(site: Site, isConnected: boolean): ISitesListDrowdownOption[] {
    const option = (
      value: SitesListDropdownOptionValue,
      label: string,
    ): ISitesListDrowdownOption => ({
      value,
      label,
      seperator: false,
      siteId: site.id,
    });

    const showInMapOption =
      this.props.onShowInMap && SiteModel.hasValidCoordinates(site) ?
        [
          {
            ...option("showInMap", this.context.formatMessage(messages.msg_show_in_map)),
            // the maintenance filter hides every marker, so there'd be nothing to focus
            disabled: this.props.onlyShowWithoutCoordinates,
          },
        ]
      : [];

    if (!isConnected) {
      return [
        option("connect", this.context.formatMessage(messages.msg_connect)),
        option("details", this.context.formatMessage(messages.msg_show_details)),
        ...showInMapOption,
        this.getDropdownSeparator(),
        this.getFavoriteDropdownOption(site),
      ];
    }

    return [
      option("dashboard", this.context.formatMessage(messages.msg_show_dashboard)),
      ...(this.props.canModifySiteSettings ?
        [option("settings", this.context.formatMessage(messages.msg_show_settings))]
      : []),
      ...showInMapOption,
      option("details", this.context.formatMessage(messages.msg_show_details)),
      this.getDropdownSeparator(),
      option("disconnect", this.context.formatMessage(messages.msg_disconnect)),
      this.getDropdownSeparator(),
      this.getFavoriteDropdownOption(site),
    ];
  }

  // default isOffline is false, since this is only relevant for the connected site!
  private renderTableRow(
    listSite: Site,
    isConnected: boolean,
    isOffline = false,
    menuPosition: "top" | "bottom" = "top",
  ) {
    // up-to-date alarm counts for the connected site
    const site =
      listSite.id === this.props.selectedSite && isSet(this.props.connectedUnackAlarms) ?
        {
          ...listSite,
          unackAlarms: this.props.connectedUnackAlarms,
          unclearedAlarms: this.props.connectedUnclearedAlarms,
        }
      : listSite;
    const isFavorite = this.props.favoriteSites?.includes(site.id) ?? false;
    const options = this.getDropdownOptions(site, isConnected);
    const onActivate = () => {
      if (isConnected) {
        this.props.onDashboardClick(this.props.navigate);
      } else {
        this.props.onSelectSite(site.id);
      }
    };

    return (
      <li
        key={site.id}
        className="sitelistentry overlaydivs"
        // 0 keeps the natural DOM tab order
        tabIndex={0}
        onClick={onActivate}
        onKeyDown={(event) => {
          // ignore keys from within the row, e.g. its option menu
          if (event.target !== event.currentTarget) {
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            onActivate();
          } else if (event.key === "Escape") {
            event.preventDefault();
            document.getElementById("searchField")?.focus();
          }
        }}
      >
        <div
          className={`siteIcon ${SiteModel.getBadgeState(site)}`}
          title={getAlarmDescription(this.context, site)}
        >
          {this.getSiteBadgeLabel(site)}
        </div>

        {/* not on the <li>, where the tooltip would also cover the menu */}
        <div
          className="mainContent"
          title={this.context.formatMessage(
            isConnected ? messages.msg_show_dashboard : messages.msg_connect,
          )}
        >
          <div className="siteNameRow">
            {isFavorite && <IconFavorite className="favoriteStar" />}
            <span className={`off${isOffline ? " offline" : ""}`}>{site?.name}</span>
            <SiteInfoTags site={site} offline={isOffline} compact />
          </div>
        </div>

        <div
          className="menuIcon"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onFocus={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <Dropdown
            align="end"
            drop={menuPosition === "top" ? "up" : "down"}
            show={this.state.openMenuSiteId === site.id}
            onToggle={(nextShow) =>
              this.setState({ openMenuSiteId: nextShow ? site.id : undefined })
            }
          >
            <Dropdown.Toggle as={SiteMenuToggle} id={`siteMenuToggle_${site.id}`} />
            <Dropdown.Menu>
              {options.map((option, index) =>
                option.seperator ?
                  <Dropdown.Divider key={`separator_${index}`} />
                : <Dropdown.Item
                    key={option.value}
                    className="siteCustomOption"
                    disabled={option.disabled}
                    onClick={() => {
                      if (option.value === "details") {
                        this.props.onDetails(site.id);
                      } else if (option.value === "showInMap") {
                        this.props.onShowInMap?.(site.id);
                      } else {
                        this.props.onDropdownSelectionChanged(option, this.props.navigate);
                      }
                    }}
                  >
                    {option.label}
                    <span className="optionIcon">{renderIconForOption(option.value)}</span>
                  </Dropdown.Item>,
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </li>
    );
  }

  private getSiteBadgeLabel(site: Site): string {
    return getInitials(site.name);
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SitesListComponent));
